import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncStatus } from '../../../../libs/utils/constants/async-status';
import {
    getEnrolledSkills,
    createSelfEnrollment,
    getEnrolledSkill,
    cancelSelfEnrollment,
} from './self-enrollment-thunk';
import { ISelfEnrollmentState } from '../../../constants/self-enrollment';

const initialState: ISelfEnrollmentState = {
    enrolledSkillsAsyncStatus: AsyncStatus.INITIAL,
    enrolledSkillAsyncStatus: AsyncStatus.INITIAL,
    enrolledSkills: [],
    createSelfEnrollmentAsyncStatus: AsyncStatus.INITIAL,
    cancelSelfEnrollmentAsyncStatus: AsyncStatus.INITIAL,
    currentEnrolledSkillId: null,
    eventSelfEnrolledCourseId: null,
};

const selfEnrollmentSlice = createSlice({
    name: 'selfEnrollment',
    initialState,
    reducers: {
        setCurrentEnrolledSkillId: (
            state,
            { payload }: PayloadAction<{ skillId: number }>
        ) => {
            state.currentEnrolledSkillId = payload.skillId;
        },
        resetSelfEnrollmentState: (state) => {
            state.createSelfEnrollmentAsyncStatus = AsyncStatus.INITIAL;
            state.enrolledSkillAsyncStatus = AsyncStatus.INITIAL;
            state.cancelSelfEnrollmentAsyncStatus = AsyncStatus.INITIAL;
        },
        setEventSelfEnrolledCourseId: (
            state,
            { payload }: PayloadAction<{ courseId: number | null }>
        ) => {
            state.eventSelfEnrolledCourseId = payload.courseId;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getEnrolledSkills.pending, (state) => {
                state.enrolledSkillsAsyncStatus = AsyncStatus.LOADING;
            })
            .addCase(getEnrolledSkills.fulfilled, (state, { payload }: any) => {
                state.enrolledSkillsAsyncStatus = AsyncStatus.SUCCESS;
                state.enrolledSkills = payload?.skills || [];
            })
            .addCase(getEnrolledSkills.rejected, (state) => {
                state.enrolledSkillsAsyncStatus = AsyncStatus.FAILURE;
                state.enrolledSkills = [];
            })
            .addCase(createSelfEnrollment.pending, (state) => {
                state.createSelfEnrollmentAsyncStatus = AsyncStatus.LOADING;
            })
            .addCase(createSelfEnrollment.fulfilled, (state) => {
                state.createSelfEnrollmentAsyncStatus = AsyncStatus.SUCCESS;
            })
            .addCase(createSelfEnrollment.rejected, (state) => {
                state.createSelfEnrollmentAsyncStatus = AsyncStatus.FAILURE;
            })
            .addCase(getEnrolledSkill.pending, (state) => {
                state.enrolledSkillAsyncStatus = AsyncStatus.LOADING;
            })
            .addCase(getEnrolledSkill.fulfilled, (state, { payload }: any) => {
                const enrolledSkill = payload?.skills?.[0];
                if (!enrolledSkill) {
                    state.enrolledSkillAsyncStatus = AsyncStatus.SUCCESS;
                    return;
                }

                const index = state.enrolledSkills.findIndex(
                    (skill) => skill.skillId === enrolledSkill.skillId
                );

                if (index >= 0) {
                    state.enrolledSkills[index] = enrolledSkill;
                } else {
                    state.enrolledSkills.push(enrolledSkill);
                }
                state.enrolledSkillAsyncStatus = AsyncStatus.SUCCESS;
            })
            .addCase(getEnrolledSkill.rejected, (state) => {
                state.enrolledSkillAsyncStatus = AsyncStatus.FAILURE;
            })
            .addCase(cancelSelfEnrollment.pending, (state) => {
                state.cancelSelfEnrollmentAsyncStatus = AsyncStatus.LOADING;
            })
            .addCase(cancelSelfEnrollment.fulfilled, (state, { meta }: any) => {
                state.cancelSelfEnrollmentAsyncStatus = AsyncStatus.SUCCESS;
                const cancelledSkillId = meta?.arg?.selfEnrollmentId;
                if (cancelledSkillId) {
                    state.enrolledSkills = state.enrolledSkills.filter(
                        (skill) => skill.selfEnrollmentId !== cancelledSkillId
                    );
                }
            })
            .addCase(cancelSelfEnrollment.rejected, (state) => {
                state.cancelSelfEnrollmentAsyncStatus = AsyncStatus.FAILURE;
            });
    },
});

export const {
    setCurrentEnrolledSkillId,
    resetSelfEnrollmentState,
    setEventSelfEnrolledCourseId,
} = selfEnrollmentSlice.actions;
export default selfEnrollmentSlice.reducer;
